import { call, put, takeEvery, select } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';

import TagService from 'shared/services/TagService';
import { ITag } from 'shared/interfaces/tag.interface';
import {
  getCurrentUserSelector,
  getAffiliateSelector
} from '../settings/settings.redux';
import {
  getAllTags,
  createTag,
  updateTag,
  deleteTag,
  associateTagWithOrder,
  disassociateTagFromOrder,
  getTagsData,
  setTagsData,
  getTagsFail,
  addTag,
  updateTagInState,
  removeTag,
  setLoading,
  setError
} from './tags.redux';
import { getOrder } from '../orders/orders.redux';

function* getAllTagsSaga(): Generator<any, void, any> {
  try {
    yield put(getTagsData());
    const tags: ITag[] = yield call(TagService.getAll);
    yield put(setTagsData(tags));
  } catch (error: any) {
    yield put(getTagsFail(error.message || 'Failed to fetch tags'));
  }
}

function* createTagSaga(
  action: PayloadAction<{
    name: string;
    color: string;
    description?: string;
    ownerModel: 'affiliate' | 'user';
    orderId?: string;
    targetModel?: string;
  }>
): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    const {
      orderId,
      ownerModel,
      targetModel = 'Order',
      ...tagData
    } = action.payload;

    const currentUser: any = yield select(getCurrentUserSelector);
    const currentAffiliate: any = yield select(getAffiliateSelector);

    const scope = {
      targetModel,
      owner: {
        model: ownerModel,
        id: ownerModel === 'user' ? currentUser?.id : currentAffiliate?.id
      }
    };

    const newTag: ITag = yield call(TagService.createTag, {
      ...tagData,
      scope
    });
    yield put(addTag(newTag));

    if (orderId) {
      yield call(TagService.associateTagWithOrder, {
        tagId: newTag.id,
        orderId
      });
      yield put(getOrder({ id: orderId, needPaymentMethod: true }));
    }

    yield put(setLoading(false));
  } catch (error: any) {
    yield put(setLoading(false));
    message.error(error?.response?.data.message || 'Failed to create tag');
  }
}

function* updateTagSaga(
  action: PayloadAction<{
    id: string;
    data: Partial<{ name: string; color: string; description?: string }>;
  }>
): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    const updatedTag: ITag = yield call(TagService.updateTag, action.payload);
    yield put(updateTagInState(updatedTag));
    yield put(setLoading(false));
    message.success('Tag updated successfully');
  } catch (error: any) {
    yield put(setError(error.message || 'Failed to update tag'));
    message.error(error?.response?.data.message || 'Failed to update tag');
  }
}

function* deleteTagSaga(
  action: PayloadAction<string>
): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield call(TagService.deleteTag, action.payload);
    yield put(removeTag(action.payload));
    yield put(setLoading(false));
    message.success('Tag deleted successfully');
  } catch (error: any) {
    yield put(setError(error.message || 'Failed to delete tag'));
    message.error(error.message || 'Failed to delete tag');
  }
}

function* associateTagWithOrderSaga(
  action: PayloadAction<{ tagId: string; orderId: string }>
): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield call(TagService.associateTagWithOrder, action.payload);
    yield put(
      getOrder({ id: action.payload.orderId, needPaymentMethod: true })
    );
    yield put(setLoading(false));
  } catch (error: any) {
    yield put(setLoading(false));
    message.error(error.message || 'Failed to add tag to order');
  }
}

function* disassociateTagFromOrderSaga(
  action: PayloadAction<{ tagId: string; orderId: string }>
): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield call(TagService.disassociateTagFromOrder, action.payload);
    yield put(
      getOrder({ id: action.payload.orderId, needPaymentMethod: true })
    );
    yield put(setLoading(false));
  } catch (error: any) {
    yield put(setLoading(false));
    message.error(error.message || 'Failed to remove tag from order');
  }
}

export default function* tagsSaga() {
  yield takeEvery(getAllTags.type, getAllTagsSaga);
  yield takeEvery(createTag.type, createTagSaga);
  yield takeEvery(updateTag.type, updateTagSaga);
  yield takeEvery(deleteTag.type, deleteTagSaga);
  yield takeEvery(associateTagWithOrder.type, associateTagWithOrderSaga);
  yield takeEvery(disassociateTagFromOrder.type, disassociateTagFromOrderSaga);
}
